import { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge, Button, Checkbox, DateRangePicker, FilterChip, Skeleton, Tag, Textarea } from '@indipia/ui-core';
import { ShareLinkPopup, shareViaKakao } from '@indipia/shared-features';
import { getSiteColor, useAuthStore } from '@indipia/shared';
import { worklogApi } from '../api/worklogApi';
import { fetchCustomerShareLink } from '../api/customerShareLinkApi';
import { useWorklogStore } from '../store/worklogStore';
import { ThumbImg } from '../components/ThumbImg';
import { PhotoLightbox } from '../components/PhotoLightbox';
import { useHolidays } from '../hooks/useHolidays';
import { useWorkTypeName } from '../hooks/useWorkTypeName';
import { presetRange, DATE_PRESETS, type DatePreset as Preset } from '../config/datePreset';
import { Toast } from '../components/Toast';
import { SitePanelProvider } from '../components/site-panel/SitePanelContext';
import { SiteArea } from '../components/site-panel/SiteArea';
import { useSiteSelection } from '../components/site-panel/useSiteSelection';
import type { BasicInfoPartner, FieldReportListItem, WorklogListItem } from '../types/worklog.types';
import { CustReqShareEditSection } from '../components/custReq/CustReqShareEditSection';
import { CustReqSharePreview } from '../components/custReq/CustReqSharePreview';
import {
  buildRequestsPayload, normalizeText, resolveReqEdit, selectAllApplied, selectedShareEntries, withSelected, withText,
  type CustReqShareItem, type ReqShareOverrides,
} from '../components/custReq/custReqCustomerShare.logic';

/**
 * 현장보고 편집·발송 (진입점5, SCR-CS-EDIT) — §1.6·§5.4.7. 설계서 §5.6 — 구 "고객리포트 설정·미리보기"
 * 화면(CustomerReportSettingPage·CustomerReportPreviewPage)을 흡수해 이 화면으로 통합됐다.
 *
 * 좌(편집 카드) + 화살표 + 우(고객공유 미리보기)를 현장보고 1건당 한 행으로 배치한다(실측 목업
 * `현장보고_편집_고객공유.html`의 `fr-pair-row`). 조회조건 바(조회기간·반장님 필터·정렬)는
 * WorklogStatusPage·WorkOrderListPage와 동일 패턴. 우측 현장목록은 현장목록 공통 룰(REQ-C-01,
 * 토글 단일선택)을 따른다 — useSiteSelection 훅의 4번째 소비 화면이 이 화면이다.
 *
 * 저장은 카드 조작(체크박스·사진토글·전체포함)마다 즉시 `setCustomerShare`로 자동 저장(초안, §4.1.11).
 * 발송은 현장당 버튼 하나로 `sendCustomerReport`(§4.1.12) — 화면에 보이는 미발송·공유내용 있는 건만 묶어 보낸다.
 */

const OWNER_AUTHOR_ID = -1;

const pad2 = (n: number) => String(n).padStart(2, '0');
const toDash = (d: Date) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const DOW = ['일', '월', '화', '수', '목', '금', '토'];
function fmtDateLabel(ymd: string) {
  const d = new Date(`${ymd}T00:00:00`);
  if (Number.isNaN(d.getTime())) return ymd;
  return `${d.getFullYear()}.${pad2(d.getMonth() + 1)}.${pad2(d.getDate())} (${DOW[d.getDay()]})`;
}
function fmtTime(iso: string) {
  const d = new Date(iso);
  const h = d.getHours();
  const m = pad2(d.getMinutes());
  const ampm = h < 12 ? '오전' : '오후';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${ampm} ${h12}:${m}`;
}
/** 게시만 되고 알림톡은 안 나간 잠금 건. lockedKind 가 없으면(구버전 서버) 기존대로 발송 완료로 본다. */
function isPublishedOnly(fr: FieldReportListItem): boolean {
  return fr.lockedKind === 'PUBLISHED';
}
function fmtSentAt(iso: string) {
  const d = new Date(iso);
  return `${pad2(d.getMonth() + 1)}.${pad2(d.getDate())} ${fmtTime(iso)}`;
}

interface DateGroup {
  key: string;
  items: FieldReportListItem[];
}
function groupByWorkDate(items: FieldReportListItem[]): DateGroup[] {
  const map = new Map<string, FieldReportListItem[]>();
  for (const fr of items) {
    const list = map.get(fr.workDate) ?? [];
    list.push(fr);
    map.set(fr.workDate, list);
  }
  return [...map.entries()].map(([key, groupItems]) => ({ key, items: groupItems }));
}

/** 카드별 편집 상태(결정 5-2) — included(보고 내용 포함 체크)·memo(고객님께 전하는 메시지)·공유 사진. 서버에는 memo·사진만 저장한다. */
interface CardEdit {
  included: boolean;
  memo: string;
  sharePhotoIds: Set<number>;
  /** 고객 요청별로 사용자가 바꾼 값(선택·고객용 문장). 없으면 기본값(켜짐·원문) */
  requests: ReqShareOverrides;
}
function initialEdit(fr: FieldReportListItem): CardEdit {
  const memo = fr.customerMemo ?? '';
  return {
    requests: {},
    included: memo.trim() !== '' && memo.trim() === fr.reportContent.trim(),
    memo,
    sharePhotoIds: new Set(fr.photos.filter((p) => p.custShareYn === 'Y').map((p) => p.frPhotoId)),
  };
}
/** 공유내용 있음 — 메시지 비공백 또는 사진 1장 이상 선택(결정 5-3 "공유내용 있는 건만"과 동일 기준, BE hasShareContent와 대응). */
function hasShareContent(edit: CardEdit): boolean {
  return edit.memo.trim() !== '' || edit.sharePhotoIds.size > 0;
}

export function CustomerReportEditPage() {
  return (
    <SitePanelProvider>
      <CustomerReportEditInner />
    </SitePanelProvider>
  );
}

function CustomerReportEditInner() {
  // ── 조회조건(기간) — WorklogStatusPage와 동일 패턴. 일반 진입 기본은 최근 7일(§4.1.6). ──
  const [preset, setPreset] = useState<Preset>('7d');
  const [customRange, setCustomRange] = useState<{ from: string; to: string } | null>(null);
  const holidays = useHolidays();
  // presetRange()는 매 호출마다 new Date()(현재 시각)로 새 Date 객체를 만든다 — 렌더마다 그대로 계산하면
  // range.from/to.getTime()이 밀리초 단위로 계속 바뀌어, 이걸 의존성으로 쓰는 조회 effect가 무한 재조회된다.
  // preset·customRange가 실제로 바뀔 때만 재계산되도록 메모이즈한다.
  const range = useMemo(
    () =>
      customRange
        ? { from: new Date(`${customRange.from}T00:00:00`), to: new Date(`${customRange.to}T00:00:00`) }
        : presetRange(preset),
    [preset, customRange],
  );

  // ── 결과 필터/정렬 ──
  const [selectedPartners, setSelectedPartners] = useState<BasicInfoPartner[]>([]);
  const [partnerPopOpen, setPartnerPopOpen] = useState(false);
  const [sortDesc, setSortDesc] = useState(true);

  // ── 작성자 목록(worklogStore 반장 재사용) — 현장보고 목록·공사현황과 동일(로그인 사장님 pseudo 포함) ──
  const storePartners = useWorklogStore((s) => s.partners);
  const worklogStoreStatus = useWorklogStore((s) => s.status);
  const partnersLoaded = worklogStoreStatus === 'ready' || worklogStoreStatus === 'error';
  const authUser = useAuthStore((s) => s.user);
  const partners = useMemo(() => {
    if (!partnersLoaded) return null;
    if (!authUser) return storePartners;
    const ownerOption: BasicInfoPartner = {
      partnerId: OWNER_AUTHOR_ID,
      partnerName: `${authUser.memberName} (나)`,
      partnerPhone: null,
      email: null,
      workTypeCodes: null,
    };
    return [ownerOption, ...storePartners];
  }, [partnersLoaded, authUser, storePartners]);

  // ── 현장 선택(REQ-C-01 공통 룰, 토글 단일선택) — 클릭된 원본 항목(현장명·고객명)도 함께 들고 있는다. ──
  const { isSiteSelected, toggleSite, selectOnlySite, onSitesLoaded } = useSiteSelection();
  const [selectedItem, setSelectedItem] = useState<WorklogListItem | null>(null);
  const [sitesLoaded, setSitesLoaded] = useState(false);

  // ── 현장보고 조회(§3.1 기간 모드) — 선택 현장 + 기간 + 작성자 필터. 단일 페이지(사이트+기간 범위라 소량). ──
  const [items, setItems] = useState<FieldReportListItem[]>([]);
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [edits, setEdits] = useState<Record<number, CardEdit>>({});
  const [editVersion, setEditVersion] = useState(0);
  const [toastMsg, setToastMsg] = useState<string | null>(null);
  const [lb, setLb] = useState<{ frId: number; photoIdx: number } | null>(null);
  const [sending, setSending] = useState(false);
  // [카카오대화방공유] 팝업 — 스케줄 [공유하기]와 같은 공용 팝업(고객 고정 링크 전용). 링크 조회는 현장 기준이다.
  const [sharePopupOpen, setSharePopupOpen] = useState(false);
  const shareSiteId = selectedItem?.siteId ?? null;
  // 새 게시 건수(결정 52) — 지금 게시하면 새로 게시될 건수. 진입·편집 저장·게시 후 publish-pending 으로 다시 읽는다.
  const [newPostCount, setNewPostCount] = useState(0);
  const [publishing, setPublishing] = useState(false);
  const pendingWorklogId = selectedItem?.worklogId ?? null;
  const refreshPending = useCallback(async () => {
    if (pendingWorklogId == null) {
      setNewPostCount(0);
      return;
    }
    try {
      const res = await worklogApi.getCustomerReportPublishPending(pendingWorklogId);
      setNewPostCount(res.data.data?.newPostCount ?? 0);
    } catch {
      // 배지는 보조 표시 — 조회 실패해도 화면 동작에는 영향 없음(기존 값 유지)
    }
  }, [pendingWorklogId]);
  useEffect(() => {
    setNewPostCount(0);
    void refreshPending();
  }, [refreshPending]);

  const baseLoaded = partnersLoaded && sitesLoaded;
  const selectedIds = useMemo(() => new Set(selectedPartners.map((p) => p.partnerId)), [selectedPartners]);

  const fetchReports = useCallback(
    (signal?: AbortSignal) => {
      if (!selectedItem) {
        setItems([]);
        setEdits({});
        setState('ready');
        return Promise.resolve();
      }
      const includeOwner = selectedIds.has(OWNER_AUTHOR_ID);
      const partnerId = [...selectedIds].filter((id) => id !== OWNER_AUTHOR_ID);
      return worklogApi
        .getFieldReports(
          {
            worklogId: selectedItem.worklogId,
            reportDateFrom: toDash(range.from),
            reportDateTo: toDash(range.to),
            partnerId: partnerId.length ? partnerId : undefined,
            includeOwner,
            sort: '-reportDtm',
            size: 100,
          },
          signal,
        )
        .then((res) => {
          const list = res.data.data.items;
          setItems(list);
          setEdits((prev) => {
            const next: Record<number, CardEdit> = {};
            for (const fr of list) next[fr.fieldReportId] = prev[fr.fieldReportId] ?? initialEdit(fr);
            return next;
          });
          setState('ready');
        });
    },
    // 정렬 토글(sortDesc)은 서버 재조회 없이 클라이언트에서 카드 순서만 반전한다(목업 DEV_RULES) — 의존성에서 제외.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedItem?.worklogId, range.from.getTime(), range.to.getTime(), selectedIds],
  );

  useEffect(() => {
    if (!baseLoaded) return;
    const controller = new AbortController();
    setState('loading');
    fetchReports(controller.signal).catch((err) => {
      if (controller.signal.aborted || err?.code === 'ERR_CANCELED') return;
      setState('error');
    });
    return () => controller.abort();
  }, [baseLoaded, fetchReports]);

  const getEdit = useCallback((fr: FieldReportListItem) => edits[fr.fieldReportId] ?? initialEdit(fr), [edits]);

  /** 잠금 가드 — 발송된 건에 조작 시도 시 토스트만 띄우고 무시(ErrorCode.FIELD_REPORT_LOCKED 메시지와 동일). */
  const guardLock = (fr: FieldReportListItem): boolean => {
    if (fr.lockedAt) {
      setToastMsg(
        isPublishedOnly(fr)
          ? '이미 고객에게 게시된 작업보고는 수정할 수 없습니다'
          : '이미 고객에게 발송된 작업보고는 수정할 수 없습니다',
      );
      return true;
    }
    return false;
  };

  /** 편집 적용 + 즉시 자동 저장(§4.1.11 초안). */
  const applyEdit = (fr: FieldReportListItem, patch: Partial<CardEdit>) => {
    const next: CardEdit = { ...getEdit(fr), ...patch };
    setEdits((prev) => ({ ...prev, [fr.fieldReportId]: next }));
    setEditVersion((v) => v + 1);
    worklogApi
      .setCustomerShare(fr.fieldReportId, {
        customerMemo: next.memo.trim() ? next.memo : null,
        sharePhotoIds: [...next.sharePhotoIds],
      })
      .then(() => void refreshPending())
      .catch(() => setToastMsg('저장에 실패했습니다. 다시 시도해주세요.'));
  };

  /** "보고 내용" 체크박스 — 켜면 보고 내용 전체를 메시지에 합치고(중복 삽입 방지), 끄면 자동 삽입분만 비운다. */
  const toggleIncluded = (fr: FieldReportListItem) => {
    if (guardLock(fr)) return;
    const cur = getEdit(fr);
    const full = fr.reportContent;
    const on = !cur.included;
    let memo = cur.memo;
    if (on) {
      if (memo.indexOf(full) < 0) memo = memo.trim() ? `${full}\n${memo}` : full;
    } else if (memo.trim() === full.trim()) {
      memo = '';
    } else if (memo.trim()) {
      setToastMsg('직접 수정한 메시지는 그대로 유지됩니다');
    }
    applyEdit(fr, { included: on, memo });
  };

  /** "작업보고 전체 고객공유하기" — 보고 내용 + 사진 전체를 한 번에 포함. */
  const shareAll = (fr: FieldReportListItem) => {
    if (guardLock(fr)) return;
    const cur = getEdit(fr);
    const full = fr.reportContent;
    let memo = cur.memo;
    if (memo.indexOf(full) < 0) memo = memo.trim() ? `${full}\n${memo}` : full;
    applyEdit(fr, {
      included: true,
      memo,
      sharePhotoIds: new Set(fr.photos.map((p) => p.frPhotoId)),
      requests: selectAllApplied(fr.customerRequests, cur.requests),
    });
  };

  const togglePhoto = (fr: FieldReportListItem, frPhotoId: number) => {
    if (guardLock(fr)) return;
    const cur = getEdit(fr);
    const nextSet = new Set(cur.sharePhotoIds);
    if (nextSet.has(frPhotoId)) nextSet.delete(frPhotoId);
    else nextSet.add(frPhotoId);
    applyEdit(fr, { sharePhotoIds: nextSet });
  };

  const updateMemo = (fr: FieldReportListItem, value: string) => {
    if (fr.lockedAt) return;
    setEdits((prev) => ({ ...prev, [fr.fieldReportId]: { ...getEdit(fr), memo: value } }));
  };
  const commitMemo = (fr: FieldReportListItem) => {
    if (fr.lockedAt) return;
    applyEdit(fr, { memo: getEdit(fr).memo });
  };

  // ── 고객 요청(S16) — 선택·고객용 문장. 서버에 저장하지 않고 화면에서만 들고 있다가 발송 본문에 실어 보낸다(확정결정 32). ──
  const toggleRequest = (fr: FieldReportListItem, item: CustReqShareItem, selected: boolean) => {
    if (guardLock(fr)) return;
    const cur = getEdit(fr);
    setEdits((prev) => ({ ...prev, [fr.fieldReportId]: { ...cur, requests: withSelected(cur.requests, item, selected) } }));
    setEditVersion((v) => v + 1);
  };
  const updateRequestText = (fr: FieldReportListItem, item: CustReqShareItem, text: string) => {
    if (fr.lockedAt) return;
    const cur = getEdit(fr);
    setEdits((prev) => ({ ...prev, [fr.fieldReportId]: { ...cur, requests: withText(cur.requests, item, text) } }));
  };
  const commitRequestText = (fr: FieldReportListItem, item: CustReqShareItem) => {
    if (fr.lockedAt) return;
    const cur = getEdit(fr);
    const text = normalizeText(resolveReqEdit(item, cur.requests).text, item);
    setEdits((prev) => ({ ...prev, [fr.fieldReportId]: { ...cur, requests: withText(cur.requests, item, text) } }));
    setEditVersion((v) => v + 1);
  };

  // ── 날짜 그룹 — 정렬 토글은 서버 재조회 없이 날짜 그룹 순서만 반전한다(목업 DEV_RULES "서버 재조회 없이 reverse"). ──
  const groups = useMemo(() => {
    const g = groupByWorkDate(items);
    g.sort((a, b) => (sortDesc ? b.key.localeCompare(a.key) : a.key.localeCompare(b.key)));
    return g;
  }, [items, sortDesc]);

  // ── 현장 발송 요약·대상 ──
  const publishedCount = items.filter((fr) => fr.lockedAt && isPublishedOnly(fr)).length;
  const sentCount = items.filter((fr) => fr.lockedAt && !isPublishedOnly(fr)).length;
  const sendTargets = items.filter((fr) => !fr.lockedAt && hasShareContent(getEdit(fr)));
  const unsentCount = sendTargets.length;
  // 알림톡 보내기 활성 — 미게시 발송 대상이 있거나, 게시만 되고 알림톡이 안 나간 건이 있을 때(서버: 새 게시 없이 알림톡만 발송 가능)
  const canSendAlimtalk = unsentCount > 0 || publishedCount > 0;
  const summaryParts: string[] = [];
  if (publishedCount) summaryParts.push(`${publishedCount}건 게시`);
  if (sentCount) summaryParts.push(`${sentCount}건 발송완료`);
  if (unsentCount) summaryParts.push(`${unsentCount}건 발송대기`);

  // 발송 중복방지키 — 대상 목록·편집 내용이 바뀌면 재발급(§4.1.12). 현장 전환 시에도 새로 발급한다.
  const [requestKey, setRequestKey] = useState(() => crypto.randomUUID());
  useEffect(() => {
    setRequestKey(crypto.randomUUID());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItem?.worklogId, editVersion]);

  const sendToCustomer = async () => {
    if (!selectedItem || !canSendAlimtalk || sending) return;
    setSending(true);
    try {
      // 고객 요청 선정(선택·고객용 문장)은 발송 본문에 보고별로 실어 보낸다(K1 3.10). 반영된 요청이 없는 보고는 항목 없음 = 서버 기본 처리.
      const requests = sendTargets.flatMap((fr) =>
        (buildRequestsPayload(fr.customerRequests, getEdit(fr).requests) ?? []).map((r) => ({ fieldReportId: fr.fieldReportId, ...r })),
      );
      const res = await worklogApi.sendCustomerReport(selectedItem.worklogId, {
        fieldReportIds: sendTargets.map((fr) => fr.fieldReportId),
        requestKey,
        ...(requests.length > 0 ? { requests } : {}),
      });
      setToastMsg(res.data.message ?? `작업보고 ${res.data.data.sentCount}건을 고객에게 발송했습니다.`);
      await fetchReports();
      void refreshPending();
    } catch (err: unknown) {
      // 알림톡 발송 가능 기간 경과·보낼 리포트 없음은 안내 문구로 처리(서버 message 보다 우선)
      const code = (err as { response?: { data?: { code?: string } } })?.response?.data?.code;
      const message =
        code === 'SHARE_SEND_WINDOW_CLOSED'
          ? '공사 종료일에서 7일이 지나 알림톡은 보낼 수 없어요. 링크를 복사해 직접 보내 주세요.'
          : code === 'NO_SENDABLE_REPORT'
            ? '보낼 수 있는 작업보고가 없어요.'
            : ((err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
              '발송에 실패했습니다. 다시 시도해주세요.');
      setToastMsg(message);
    } finally {
      setSending(false);
    }
  };

  /**
   * [카카오대화방공유](결정 29·52) — 새 소식을 게시(고정본 생성)한 뒤 고객 링크 팝업을 연다.
   * 게시할 새 소식이 없으면(published=false) 게시는 건너뛰고 팝업만 연다. 게시 실패 시 팝업 대신 오류 알림.
   */
  const shareToKakao = async () => {
    if (!selectedItem || publishing) return;
    setPublishing(true);
    try {
      const res = await worklogApi.publishCustomerReport(selectedItem.worklogId);
      if (res.data.data?.published) {
        await fetchReports(); // 게시된 건은 잠금 표시로 바뀐다
      }
      void refreshPending();
      setSharePopupOpen(true);
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        '게시에 실패했습니다. 다시 시도해주세요.';
      setToastMsg(message);
    } finally {
      setPublishing(false);
    }
  };

  const partnerLabel = selectedPartners.length > 0 ? `반장님 ${selectedPartners.length}` : '반장님';

  const lbFr = useMemo(() => items.find((f) => f.fieldReportId === lb?.frId) ?? null, [items, lb]);

  return (
    <div className="flex h-full">
      <div className="flex min-w-0 flex-1 flex-col bg-surface">
        {/* ── 조회조건 바(기간·반장님 필터·정렬) ── */}
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-6 py-3">
          <div className="flex flex-wrap items-center gap-2">
            <DateRangePicker
              value={{ start: range.from, end: range.to }}
              onChange={({ start, end }) => {
                if (start && end) setCustomRange({ from: toDash(start), to: toDash(end) });
              }}
              active={!!customRange}
              holidays={holidays}
              presets={false}
              aria-label="기간 직접 설정"
            />
            {DATE_PRESETS.map(([key, label]) => (
              <FilterChip
                key={key}
                active={!customRange && preset === key}
                onClick={() => {
                  setCustomRange(null);
                  setPreset(key);
                }}
              >
                {label}
              </FilterChip>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <div className="relative">
              <button
                onClick={() => setPartnerPopOpen((v) => !v)}
                className={[
                  'flex items-center gap-1.5 rounded-full px-3 py-1.5 text-body-md font-medium transition-colors',
                  selectedPartners.length
                    ? 'bg-group-subtle text-group'
                    : 'border border-border bg-surface-raised text-text-secondary hover:bg-surface-muted',
                ].join(' ')}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
                  <path d="M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" /><circle cx="8.5" cy="7" r="4" /><line x1="20" y1="8" x2="20" y2="14" /><line x1="23" y1="11" x2="17" y2="11" />
                </svg>
                {partnerLabel}
              </button>
              {partnerPopOpen && (
                <PartnerFilterPopover
                  selected={selectedPartners}
                  partners={partners}
                  error={worklogStoreStatus === 'error'}
                  onClose={() => setPartnerPopOpen(false)}
                  onApply={(list) => {
                    setSelectedPartners(list);
                    setPartnerPopOpen(false);
                  }}
                />
              )}
            </div>

            <Button variant="etc" size="md" onClick={() => setSortDesc((v) => !v)} aria-label="정렬">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round">
                <path d="M12 5v14" />
                {sortDesc ? <path d="M5 12l7-7 7 7" /> : <path d="M19 12l-7 7-7-7" />}
              </svg>
              {sortDesc ? '최신순' : '과거순'}
            </Button>
          </div>
        </div>

        {/* ── 본문 ── */}
        <div className="flex-1 overflow-auto px-6 pb-10">
          {state === 'loading' && (
            <div className="space-y-3 pt-3">
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} height={160} className="rounded-lg" />
              ))}
            </div>
          )}

          {state === 'error' && (
            <div className="mt-6 flex flex-col items-center gap-3 rounded-lg border border-border bg-surface-raised py-16">
              <span className="text-sm text-text-secondary">현장보고를 불러오지 못했습니다.</span>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  setState('loading');
                  fetchReports().catch(() => setState('error'));
                }}
              >
                다시 조회
              </Button>
            </div>
          )}

          {state === 'ready' && items.length === 0 && (
            <div className="mt-6 flex flex-col items-center gap-2 rounded-lg border border-border bg-surface-raised py-20 text-center">
              <div className="text-heading-sm font-bold text-text">공유할 작업보고가 없습니다</div>
              <div className="text-body-md leading-relaxed text-text-secondary">
                오른쪽에서 현장을 선택하거나 조회기간을 바꿔보세요.
              </div>
            </div>
          )}

          {state === 'ready' && items.length > 0 && selectedItem && (
            <div className="pt-3">
              {/* 현장 헤더 — 현장명·고객명 + 발송완료/발송대기 요약 + 발송 버튼(REQ-5-?, §5.4.7).
                  목업 라벨은 "미공유"지만 사장님 피드백으로 "발송대기"로 변경(의미는 동일 — 공유내용은 있으나 아직 미발송). */}
              <div className="mb-4 flex flex-wrap items-center gap-2.5 rounded-lg border border-border bg-surface-raised px-4 py-3">
                <span className="h-2 w-2 shrink-0 rounded-full bg-info" aria-hidden />
                <span className="text-body-lg font-bold text-text">{selectedItem.siteName}</span>
                {selectedItem.customerName && (
                  <span className="text-body-sm text-text-secondary">{selectedItem.customerName} 고객님</span>
                )}
                {summaryParts.length > 0 && (
                  <span className="text-body-sm text-text-muted">{summaryParts.join(' · ')}</span>
                )}
                <div className="ml-auto flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={shareSiteId == null}
                    loading={publishing}
                    onClick={() => void shareToKakao()}
                  >
                    카카오대화방공유
                    {newPostCount > 0 && (
                      <Badge status="info" className="ml-1" aria-label={`새 소식 ${newPostCount}건`}>
                        새 소식 {newPostCount}
                      </Badge>
                    )}
                  </Button>
                  <Button
                    size="sm"
                    disabled={!canSendAlimtalk}
                    loading={sending}
                    title={!canSendAlimtalk ? '발송할 새 작업보고가 없습니다 (발송된 건은 수정·재발송 불가)' : undefined}
                    onClick={sendToCustomer}
                  >
                    알림톡 보내기
                  </Button>
                </div>
              </div>

              {shareSiteId != null && (
                <ShareLinkPopup
                  open={sharePopupOpen}
                  onClose={() => setSharePopupOpen(false)}
                  siteId={selectedItem.siteId}
                  fetchLink={() => fetchCustomerShareLink(shareSiteId)}
                  notice="새 소식이 없어도 같은 링크로 최신 화면이 열려요"
                  onKakaoShare={(url) => shareViaKakao({
                    url,
                    title: `우리집 공사 소식 · ${selectedItem.siteName}`,
                    description: authUser?.compName ? `${authUser.compName}에서 공유한 공사 소식이에요` : '공유된 공사 소식이에요',
                    imageUrl: `${window.location.origin}/brand/indipia-logo.png`,
                  })}
                />
              )}

              <div className="space-y-6">
                {groups.map((g) => (
                  <div key={g.key}>
                    <div className="mb-2 flex items-center gap-2">
                      <span className="text-body-md font-bold text-text">{fmtDateLabel(g.key)}</span>
                      <span className="h-px flex-1 bg-border" />
                    </div>
                    <div className="space-y-3">
                      {g.items.map((fr) => (
                        <EditPairRow
                          key={fr.fieldReportId}
                          fr={fr}
                          edit={getEdit(fr)}
                          onToggleIncluded={() => toggleIncluded(fr)}
                          onShareAll={() => shareAll(fr)}
                          onTogglePhoto={(frPhotoId) => togglePhoto(fr, frPhotoId)}
                          onMemoChange={(v) => updateMemo(fr, v)}
                          onMemoBlur={() => commitMemo(fr)}
                          onOpenPhoto={(idx) => setLb({ frId: fr.fieldReportId, photoIdx: idx })}
                          onToggleRequest={(item, on) => toggleRequest(fr, item, on)}
                          onRequestTextChange={(item, text) => updateRequestText(fr, item, text)}
                          onRequestTextCommit={(item) => commitRequestText(fr, item)}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 우측 현장 목록 패널 */}
      <SiteArea
        onFirstLoad={() => setSitesLoaded(true)}
        isSiteSelected={isSiteSelected}
        onToggleSite={(siteId, item) => {
          toggleSite(siteId);
          setSelectedItem((prev) => (prev?.siteId === siteId ? null : item));
        }}
        onSitesLoaded={(siteIds, listItems) => {
          onSitesLoaded(siteIds, listItems);
          setSelectedItem((prev) => prev ?? listItems.find((it) => it.siteId === siteIds[0]) ?? null);
        }}
        onSiteRegistered={(siteId) => selectOnlySite(siteId)}
        onSiteDeleted={(siteId) => {
          if (siteId === selectedItem?.siteId) setSelectedItem(null);
        }}
      />

      <PhotoLightbox
        photos={(lbFr?.photos ?? []).map((p) => ({ url: p.url || p.thumbUrl }))}
        index={lb?.photoIdx ?? null}
        onClose={() => setLb(null)}
        onIndexChange={(i) => setLb((prev) => (prev ? { ...prev, photoIdx: i } : prev))}
        renderImage={(photo, style) => <ThumbImg url={photo.url} alt="" imgStyle={style} />}
      />

      <Toast message={toastMsg} onClose={() => setToastMsg(null)} variant="info" />
    </div>
  );
}

const LockIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0110 0v4" />
  </svg>
);
const ClockIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
  </svg>
);
const SendIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" />
  </svg>
);
const ArrowIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
  </svg>
);

/**
 * 현장보고 1건 — 좌(편집 카드) · 화살표 · 우(고객공유 미리보기). 실측 목업 `fr-pair-row` 그대로 좌우 배치.
 */
function EditPairRow({
  fr,
  edit,
  onToggleIncluded,
  onShareAll,
  onTogglePhoto,
  onMemoChange,
  onMemoBlur,
  onOpenPhoto,
  onToggleRequest,
  onRequestTextChange,
  onRequestTextCommit,
}: {
  fr: FieldReportListItem;
  edit: CardEdit;
  onToggleIncluded: () => void;
  onShareAll: () => void;
  onTogglePhoto: (frPhotoId: number) => void;
  onMemoChange: (value: string) => void;
  onMemoBlur: () => void;
  onOpenPhoto: (photoIdx: number) => void;
  onToggleRequest: (item: CustReqShareItem, selected: boolean) => void;
  onRequestTextChange: (item: CustReqShareItem, text: string) => void;
  onRequestTextCommit: (item: CustReqShareItem) => void;
}) {
  const locked = !!fr.lockedAt;
  const lockedLabel = isPublishedOnly(fr) ? '게시' : '발송';
  const reqEntries = selectedShareEntries(fr.customerRequests, edit.requests);
  const workTypeNames = fr.workTypeNames?.length ? fr.workTypeNames : fr.workTypeCodes;
  const includedPhotos = fr.photos.filter((p) => edit.sharePhotoIds.has(p.frPhotoId));
  const hasAnyShare = edit.memo.trim() !== '' || includedPhotos.length > 0;

  return (
    <div className="flex items-start gap-3">
      {/* 좌 — 편집 카드 */}
      <article className={['min-w-0 flex-1 rounded-lg border bg-surface-raised p-4', locked ? 'border-border' : 'border-border'].join(' ')}>
        <div className="flex flex-wrap items-center gap-1.5">
          <Badge status="success">작업보고</Badge>
          <span className="flex shrink-0 items-center gap-1 text-caption text-text-muted">
            <ClockIcon />
            {fmtTime(fr.reportDtm)}
          </span>
          <span className="shrink-0 text-body-md font-semibold text-text">{fr.writerName ?? '-'}</span>
          {workTypeNames.map((name, i) => (
            <Badge key={`wt-${name}-${i}`} status="info">{name}</Badge>
          ))}
          {locked && (
            <Badge status="success" className="ml-auto flex shrink-0 items-center gap-1">
              <LockIcon /> {lockedLabel}완료
            </Badge>
          )}
        </div>

        <div className="mt-3">
          <div className="mb-1 text-caption font-bold text-text-secondary">
            보고 내용
            <span className="ml-2 font-medium text-text-muted">
              {locked ? `${lockedLabel} 완료` : '선택하면 고객님께 전하는 메시지에 전체가 들어갑니다'}
            </span>
          </div>
          <label
            className={[
              'flex cursor-pointer items-start gap-2 rounded-md border px-3 py-2.5',
              edit.included ? 'border-[var(--indipia-purple-500)] bg-[var(--indipia-purple-50)]' : 'border-border bg-surface',
              locked ? 'cursor-not-allowed opacity-70' : 'hover:bg-surface-muted',
            ].join(' ')}
          >
            <Checkbox bare className="mt-0.5 shrink-0" checked={edit.included} disabled={locked} onChange={onToggleIncluded} />
            <span className="whitespace-pre-line text-body-sm leading-relaxed text-text">{fr.reportContent}</span>
          </label>
        </div>

        {fr.photos.length > 0 && (
          <div className="mt-2.5 grid grid-cols-6 gap-2">
            {fr.photos.map((p, pi) => {
              const on = edit.sharePhotoIds.has(p.frPhotoId);
              return (
                <button
                  key={p.frPhotoId}
                  type="button"
                  aria-label={`사진 ${pi + 1} 크게보기`}
                  onClick={() => onOpenPhoto(pi)}
                  className="relative aspect-square overflow-hidden rounded-lg p-0"
                >
                  <ThumbImg url={p.thumbUrl} className={['h-full w-full object-cover', on ? '' : 'opacity-40'].join(' ')} />
                  <span
                    role="button"
                    aria-label={locked ? `${lockedLabel} 완료 — 수정 불가` : '고객공유 포함/제외'}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!locked) onTogglePhoto(p.frPhotoId);
                    }}
                    className={[
                      'absolute right-1.5 top-1.5 flex h-5 w-5 items-center justify-center rounded-full border text-caption font-bold leading-none shadow-sm',
                      on
                        ? 'border-[var(--indipia-purple-500)] bg-[var(--indipia-purple-500)] text-white'
                        : 'border-border bg-[rgba(255,255,255,0.92)] text-[var(--indipia-purple-500)]',
                      locked ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
                    ].join(' ')}
                  >
                    {on ? '✓' : ''}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        <CustReqShareEditSection
          items={fr.customerRequests ?? []}
          overrides={edit.requests}
          locked={locked}
          hasShareContent={hasAnyShare}
          onToggle={onToggleRequest}
        />

        <div className="mt-3">
          {locked ? (
            <div className="flex items-center gap-1.5 rounded-md bg-surface px-3 py-2 text-caption font-semibold text-text-secondary">
              <LockIcon /> 고객 {lockedLabel} 완료 · 수정 불가 ({fmtSentAt(fr.lockedAt as string)})
            </div>
          ) : (
            <button
              type="button"
              onClick={onShareAll}
              className="flex w-full items-center justify-center gap-1.5 rounded-md px-3 py-2 text-body-sm font-semibold text-[var(--indipia-purple-500)] hover:bg-[var(--indipia-purple-50)]"
            >
              <SendIcon />
              작업보고 전체 고객공유하기
            </button>
          )}
        </div>
      </article>

      <div className="mt-6 shrink-0 text-text-muted" aria-hidden>
        <ArrowIcon />
      </div>

      {/* 우 — 고객공유 미리보기 */}
      <article className="min-w-0 flex-1 rounded-lg border border-[var(--indipia-purple-200)] bg-[var(--indipia-purple-50)] p-4">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="rounded-full border border-[var(--indipia-purple-200)] bg-white px-3 py-[2px] text-caption font-bold text-[var(--indipia-purple-500)]">
            고객공유 미리보기
          </span>
          {workTypeNames.length > 0 && <Badge status="info">{workTypeNames[0]}</Badge>}
          {locked ? (
            <span className="ml-auto flex shrink-0 items-center gap-1 rounded-full bg-[var(--color-successSubtle)] px-2 py-[2px] text-caption font-semibold text-success">
              <LockIcon /> {lockedLabel}완료 · {fmtSentAt(fr.lockedAt as string)}
            </span>
          ) : hasAnyShare ? (
            <span className="ml-auto shrink-0 rounded-full bg-surface-muted px-2 py-[2px] text-caption font-semibold text-text-muted">
              발송대기
            </span>
          ) : null}
        </div>

        {/* 메시지 입력칸을 사진 위에 배치(DEV_RULES: 고객 화면 F-2와 동일 순서) */}
        <div className="mt-2.5">
          <div className="mb-1 flex items-center gap-1 text-caption font-semibold text-[var(--indipia-purple-500)]">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
            </svg>
            고객님께 전하는 메시지
          </div>
          <Textarea
            className="field-auto"
            value={edit.memo}
            readOnly={locked}
            onChange={(e) => onMemoChange(e.target.value)}
            onBlur={onMemoBlur}
            placeholder="왼쪽에서 보고 내용을 선택하거나 고객님께 전달할 메시지를 입력하세요."
          />
        </div>

        {includedPhotos.length > 0 && (
          <div className="mt-2.5 grid grid-cols-6 gap-2">
            {includedPhotos.map((p) => (
              <div key={p.frPhotoId} className="aspect-square overflow-hidden rounded-lg">
                <ThumbImg url={p.thumbUrl} className="h-full w-full object-cover" />
              </div>
            ))}
          </div>
        )}

        <CustReqSharePreview
          entries={reqEntries}
          locked={locked}
          onTextChange={onRequestTextChange}
          onTextCommit={onRequestTextCommit}
        />

        {!hasAnyShare && (
          <div className="mt-3 rounded-md border border-dashed border-border py-6 text-center text-body-sm text-text-muted">
            공유할 항목이 없습니다.
            <br />
            왼쪽 카드에서 보고 내용이나 사진을 선택하세요.
          </div>
        )}
      </article>
    </div>
  );
}

/** 팝오버 행에서 바로 보여주는 공종 칩 최대 개수 — 초과분은 '+N' */
const MAX_TRADE_CHIPS = 3;

/** 반장 다중선택 팝오버 — WorklogStatusPage·WorkOrderListPage 동명 컴포넌트와 동일 UX. */
function PartnerFilterPopover({
  selected,
  partners,
  error,
  onApply,
  onClose,
}: {
  selected: BasicInfoPartner[];
  partners: BasicInfoPartner[] | null;
  error: boolean;
  onApply: (list: BasicInfoPartner[]) => void;
  onClose: () => void;
}) {
  const [checked, setChecked] = useState<Set<number>>(new Set(selected.map((p) => p.partnerId)));

  const workTypeName = useWorkTypeName();

  const toggle = (id: number) =>
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const apply = () => onApply((partners ?? []).filter((p) => checked.has(p.partnerId)));

  return (
    <>
      <div className="fixed inset-0 z-dropdown" onClick={onClose} aria-hidden />
      <div className="absolute left-0 top-full z-dropdown mt-1 w-64 rounded-lg border border-border bg-surface-raised p-3 shadow-lg">
        <div className="mb-2 flex items-center justify-between">
          <span className="text-body-md font-semibold text-text">작성자 필터</span>
          <Button variant="ghost" size="sm" onClick={() => setChecked(new Set())}>
            전체 해제
          </Button>
        </div>
        {/* 가로 스크롤 없음(overflow-x-hidden) — 칩이 많아도 목록 폭 안에서 줄바꿈한다 */}
        <div className="max-h-56 space-y-0.5 overflow-y-auto overflow-x-hidden">
          {partners === null && !error && (
            <div className="py-6 text-center text-body-sm text-text-muted">불러오는 중…</div>
          )}
          {error && <div className="py-6 text-center text-body-sm text-text-muted">작성자 목록을 불러오지 못했습니다.</div>}
          {partners?.length === 0 && (
            <div className="py-6 text-center text-body-sm text-text-muted">등록된 작성자가 없습니다.</div>
          )}
          {partners?.map((p) => (
            // 행 레이아웃: [체크박스][아바타][이름 / 공종 칩(아래로 줄바꿈)] — 공종이 많은 반장도 이름이 맨 위에 보이도록
            // 위쪽 기준(items-start)으로 맞춘다(칩을 이름 옆 좁은 열에 두면 행이 길어져 세로 중앙의 이름·체크박스가 가려졌다).
            <label key={p.partnerId} className="flex cursor-pointer items-start gap-2.5 rounded-md px-2 py-2 hover:bg-surface-muted">
              {/* h-7(=아바타 높이)로 감싸 첫 줄(아바타·이름) 중심에 맞춘다 */}
              <span className="flex h-7 shrink-0 items-center">
                <Checkbox bare checked={checked.has(p.partnerId)} onChange={() => toggle(p.partnerId)} />
              </span>
              <span
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-body-sm font-bold text-white"
                style={{ background: getSiteColor(p.partnerId) }}
              >
                {p.partnerName.charAt(0)}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex h-7 items-center text-body-md font-medium text-text">{p.partnerName}</div>
                {/* 공종 칩 — 이름 아래에서 줄바꿈. 앞 MAX_TRADE_CHIPS개만 보이고 나머지는 '+N'(전체는 hover title) */}
                {p.workTypeCodes && (() => {
                  const names = p.workTypeCodes.split(',').map((code) => workTypeName.get(code) ?? code);
                  const rest = names.length - MAX_TRADE_CHIPS;
                  return (
                    <div className="flex flex-wrap gap-02 pb-01" title={names.join(', ')}>
                      {names.slice(0, MAX_TRADE_CHIPS).map((name, i) => (
                        <Tag key={`${name}-${i}`} className="!px-1.5 !py-[1px] !text-caption">
                          {name}
                        </Tag>
                      ))}
                      {rest > 0 && <Tag className="!px-1.5 !py-[1px] !text-caption">+{rest}</Tag>}
                    </div>
                  );
                })()}
              </div>
            </label>
          ))}
        </div>
        <div className="mt-3 flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose}>취소</Button>
          <Button size="sm" onClick={apply}>적용</Button>
        </div>
      </div>
    </>
  );
}
